import React, { useCallback } from 'react';
import { TAdditionalApplications } from 'types';
import { IEditRestaurantState } from '../../../../../../../../editRestaurantState/editRestaurantContext';
import Text from 'components/Text/Text';
import SourceIdField from './components/SourceIdField';
import IsvSourceIdField from './components/IsvSourceIdField';
import IsvPercenField from './components/IsvPercenField';
import IsvFixedFeeAmountField from './components/IsvFixedFeeAmountField';
import IsvFixedFeeThresholdField from './components/IsvFixedFeeThresholdField';
import PinCodeField from './components/PinCodeField';
import FranchiseFeeCheckbox from './components/FranchiseFeeCheckbox';
import FranchisePercentField from './components/FranchisePercentField';
import FranchiseFixedFeeAmountField from './components/FranchiseFixedFeeAmountField';
import FranchiseFixedFeeThresholdField from './components/FranchiseFixedFeeThresholdField';
import style from './VivaWallet.module.scss';

interface IProps {
	changeVivaWalletIsv: ({
		key,
		value,
	}: {
		key: keyof IEditRestaurantState['vivaWalletIsv'];
		value?: string | number | null;
	}) => void;
	additionalApplications: TAdditionalApplications[];
	isFranchiseFeeSupported: boolean;
}

const VivaWallet: React.FC<IProps> = ({ changeVivaWalletIsv, additionalApplications, isFranchiseFeeSupported }) => {
	const handleChangeSourceId = useCallback(
		(value: string) => {
			changeVivaWalletIsv({ key: 'sourceId', value });
		},
		[changeVivaWalletIsv],
	);

	const handleChangeIsvSourceId = useCallback(
		(value: string) => {
			changeVivaWalletIsv({ key: 'isvSourceId', value });
		},
		[changeVivaWalletIsv],
	);

	const handleChangeIsvPercen = useCallback(
		(value: Nullable<number>) => {
			changeVivaWalletIsv({ key: 'isvPercent', value });
		},
		[changeVivaWalletIsv],
	);

	const handleChangeIsvFixedFeeAmount = useCallback(
		(value: Nullable<number>) => {
			changeVivaWalletIsv({ key: 'isvFixedFeeAmount', value });
		},
		[changeVivaWalletIsv],
	);

	const handleChangeIsvFixedFeeThreshold = useCallback(
		(value: Nullable<number>) => {
			changeVivaWalletIsv({ key: 'isvFixedFeeThreshold', value });
		},
		[changeVivaWalletIsv],
	);

	const handleChangePinCode = useCallback(
		(value: string) => {
			changeVivaWalletIsv({ key: 'pinCode', value });
		},
		[changeVivaWalletIsv],
	);

	const handleToggleFranchiseFee = useCallback(() => {
		changeVivaWalletIsv({ key: 'franchiseFeeSupported' });
	}, [changeVivaWalletIsv]);

	const handleChangeFranchisePercent = useCallback(
		(value: Nullable<number>) => {
			changeVivaWalletIsv({ key: 'franchisePercent', value });
		},
		[changeVivaWalletIsv],
	);

	const handleChangeFranchiseFixedFeeAmount = useCallback(
		(value: Nullable<number>) => {
			changeVivaWalletIsv({ key: 'franchiseFixedFeeAmount', value });
		},
		[changeVivaWalletIsv],
	);

	const handleChangeFranchiseFixedFeeThreshold = useCallback(
		(value: Nullable<number>) => {
			changeVivaWalletIsv({ key: 'franchiseFixedFeeThreshold', value });
		},
		[changeVivaWalletIsv],
	);

	return (
		<>
			{additionalApplications.includes('VivaWallet') && (
				<>
					<Text
						translationNameSpace="form"
						translationKey="vivaWalletConfigurationFieldsHeading"
						theme="secondary"
						size="s"
						weight="bold"
						display="inline"
					/>
					<div className={style.row}>
						<SourceIdField changeSourceId={handleChangeSourceId} />
						<IsvSourceIdField changeIsvSourceId={handleChangeIsvSourceId} />
					</div>
					<div className={style.row}>
						<IsvPercenField changeIsvPercenField={handleChangeIsvPercen} />
						<IsvFixedFeeAmountField changeIsvFixedFeeAmount={handleChangeIsvFixedFeeAmount} />
					</div>
					<div className={style.row}>
						<IsvFixedFeeThresholdField changeIsvFixedFeeThreshold={handleChangeIsvFixedFeeThreshold} />
						<PinCodeField changePinCode={handleChangePinCode} />
					</div>
					<div className={style.row}>
						<FranchiseFeeCheckbox
							isFranchiseFeeSupported={isFranchiseFeeSupported}
							onToggle={handleToggleFranchiseFee}
						/>
						<div />
					</div>
					<div className={style.row}>
						<FranchisePercentField
							onChange={handleChangeFranchisePercent}
							disabled={!isFranchiseFeeSupported}
						/>
						<FranchiseFixedFeeAmountField
							onChange={handleChangeFranchiseFixedFeeAmount}
							disabled={!isFranchiseFeeSupported}
						/>
					</div>
					<div className={style.row}>
						<FranchiseFixedFeeThresholdField
							onChange={handleChangeFranchiseFixedFeeThreshold}
							disabled={!isFranchiseFeeSupported}
						/>
					</div>
				</>
			)}
		</>
	);
};

export default React.memo(VivaWallet);
